// Read-only view of an app's store package for the Apps page: the listing text
// and screenshots of every locale, the icon and the feature graphic. Developers
// taking an app into work look here; editing stays on Apps Setup → Share.
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { getStoreAssets, getStoreListings, storeAssetUrl } from '@/api/app-handoff';
import { ListingChangesSection } from './ListingChangesSection';

interface Props {
  app: { id: string; name: string; icon_url?: string | null } | null;
  onClose: () => void;
}

function CopyField({ label, value, max, multiline = false }: { label: string; value: string; max: number; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — the text is still selectable */ }
  };
  return (
    <div className="border-b border-border px-4 py-3 last:border-b-0">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-content-subtle">{label}</span>
        <span className="flex items-center gap-3">
          <span className={`text-[11px] ${value.length > max ? 'text-warning' : 'text-content-subtle'}`}>{value.length} / {max}</span>
          <button type="button" onClick={copy} className="text-xs text-primary hover:underline">{copied ? 'Copied' : 'Copy'}</button>
        </span>
      </div>
      <p className={`mt-1 text-sm text-content break-words ${multiline ? 'whitespace-pre-wrap leading-6' : ''}`}>{value || '—'}</p>
    </div>
  );
}

export function AppPackageModal({ app, onClose }: Props) {
  const appId = app?.id ?? '';
  const listings = useQuery({ queryKey: ['store-listings', appId], queryFn: () => getStoreListings(appId), enabled: !!appId });
  const assets = useQuery({ queryKey: ['store-assets', appId], queryFn: () => getStoreAssets(appId), enabled: !!appId });
  const [lang, setLang] = useState('');

  useEffect(() => { setLang(''); }, [appId]);

  const loadError = listings.error ?? assets.error;
  const all = listings.data ?? [];
  const feature = (assets.data ?? []).find((a) => a.kind === 'feature');
  // Locales that only have frames (no text row) still get a tab.
  const langs = [...new Set([...all.map((l) => l.lang), ...(assets.data ?? []).filter((a) => a.kind === 'screenshot').map((a) => a.lang)])];
  const activeLang = lang || langs[0] || '';
  const current = all.find((l) => l.lang === activeLang);
  const shots = (assets.data ?? []).filter((a) => a.kind === 'screenshot' && a.lang === activeLang);

  return (
    <Modal isOpen={!!app} onClose={onClose} title={`Store package — ${app?.name ?? ''}`} size="lg">
      {listings.isLoading || assets.isLoading ? (
        <p className="py-6 text-center text-sm text-content-subtle">Loading…</p>
      ) : loadError ? (
        <p className="py-6 text-center text-sm text-danger">
          {loadError instanceof Error ? loadError.message : 'Failed to load the package'}
        </p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            {app?.icon_url
              ? <img src={app.icon_url} alt="" className="h-14 w-14 rounded-xl border border-border object-cover" />
              : <div className="grid h-14 w-14 place-items-center rounded-xl border border-dashed border-border text-[10px] text-content-subtle">no icon</div>}
            {feature && (
              <a href={storeAssetUrl(feature.media_key)} target="_blank" rel="noreferrer" title="Feature graphic">
                <img src={storeAssetUrl(feature.media_key)} alt="Feature graphic" className="h-14 rounded-lg border border-border object-cover" />
              </a>
            )}
          </div>

          {langs.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-xs uppercase tracking-wide text-content-subtle">Locale</span>
              {langs.map((l) => {
                const listing = all.find((x) => x.lang === l);
                const active = activeLang === l;
                return (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setLang(l)}
                    className={`rounded-md border px-2.5 py-1 text-xs ${active ? 'border-primary bg-primary text-white' : 'border-border bg-surface text-content-muted hover:text-content'}`}
                  >
                    {l}{listing?.is_source ? ' · source' : ''}{listing && !listing.fits ? ' ⚠' : ''}
                  </button>
                );
              })}
            </div>
          )}

          {current ? (
            <div className="rounded-lg border border-border bg-surface">
              <CopyField label="Title" value={current.title} max={30} />
              <CopyField label="Short description" value={current.short_description} max={80} />
              <CopyField label="Full description" value={current.full_description} max={4000} multiline />
            </div>
          ) : (
            <p className="text-sm text-content-subtle">No listing text for {activeLang || 'this app'}.</p>
          )}

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-subtle">
              Screenshots{activeLang ? ` · ${activeLang}` : ''} ({shots.length})
            </p>
            {shots.length === 0 ? (
              <p className="text-sm text-content-subtle">No screenshots for this locale.</p>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {shots.map((s) => (
                  <a key={s.id} href={storeAssetUrl(s.media_key)} target="_blank" rel="noreferrer" title={s.caption ?? undefined}>
                    <img src={storeAssetUrl(s.media_key)} alt={s.caption ?? ''} className="w-full rounded-md border border-border object-cover" />
                  </a>
                ))}
              </div>
            )}
          </div>
          <p className="text-[11px] text-content-subtle">Edit texts, locales and frames on Apps Setup → Share.</p>

          <div className="border-t border-border pt-4">
            <ListingChangesSection appId={appId} />
          </div>
        </div>
      )}
    </Modal>
  );
}
